import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { LocalStorageKeys } from "src/utils/localStorageKeys";

export function usePosition() {
  const [location, setLocation] = useState<{
    latitude: number;
    longitude: number;
    fromIp: boolean;
    accuracy: number;
  }>({
    latitude: 0,
    longitude: 0,
    fromIp: true,
    accuracy: 200,
  });
  const [shouldShowPrompt, setShouldShowPrompt] = useState(false);
  
  useEffect(() => {
    // Check if prompt has been shown previously
    const promptShown = localStorage.getItem(LocalStorageKeys.locationPromptShown) === "true";
    
    const watchId = navigator.geolocation.watchPosition(
      async (position) => {
        if (position.coords.latitude !== 0 && position.coords.longitude !== 0) {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            fromIp: false,
            accuracy: position.coords.accuracy,
          });
          // If we have accurate GPS position, don't show prompt
          setShouldShowPrompt(false);
        }
      },
      async (error) => {
        const lat = Number(
          localStorage.getItem(LocalStorageKeys.latitudeFromIp)
        );
        const lon = Number(
          localStorage.getItem(LocalStorageKeys.longitudeFromIp)
        );
        if (
          lat !== 0 &&
          lon !== 0 &&
          !Number.isNaN(lat) &&
          !Number.isNaN(lon)
        ) {
          setLocation({
            latitude: lat,
            longitude: lon,
            fromIp: true,
            accuracy: 20,
          });
        } else {
          if (!localStorage.getItem(LocalStorageKeys.latitudeFromIp)) {
            await fetch(
              `https://api.geoapify.com/v1/ipinfo?apiKey=${process.env.VITE_GEO_KEY}`
            )
              .then((res) => res.json())
              .then((data) => {
                localStorage.setItem(
                  LocalStorageKeys.latitudeFromIp,
                  data.location.latitude
                );
                localStorage.setItem(
                  LocalStorageKeys.longitudeFromIp,
                  data.location.longitude
                );
                setLocation({
                  latitude: data.location.latitude,
                  longitude: data.location.longitude,
                  fromIp: true,
                  accuracy: 20,
                });
              });
          }
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      }
    );
    
    return () => {
      navigator.geolocation.clearWatch(watchId);
    };
  }, []);
  
  // Check if we should show prompt after location is set
  useEffect(() => {
    if (!location) return;
    
    const promptShown = localStorage.getItem(LocalStorageKeys.locationPromptShown) === "true";
    // Show prompt if: not shown previously AND position is inaccurate (from IP or accuracy > 100m)
    const hasInaccuratePosition = location.fromIp || (location.accuracy && location.accuracy > 100);
    
    if (!promptShown && hasInaccuratePosition) {
      setShouldShowPrompt(true);
    } else {
      setShouldShowPrompt(false);
    }
  }, [location]);
  
  const query = useMemo(() => {
    return {
      latitude: location?.latitude,
      longitude: location?.longitude,
      fromIp: location?.fromIp,
      accuracy: location?.accuracy,
      shouldShowPrompt: shouldShowPrompt
    };
  }, [location?.latitude, location?.longitude, location?.fromIp, location?.accuracy, shouldShowPrompt]);
  return query;
}
